/**
 * BigBingo endpoint and Learning Time calculation.
 */
import {DateField, type AnalyticsEvent} from "@khanacademy/event-schemas";
import type {SentryContext} from "@khanacademy/wonder-stuff-sentry";
import {formUrlEncode, khanFetch} from "@khan/data-fetch";
import Log, {Errors, type ErrorWithResponse} from "@khan/logging";
import {DeployInfo, RequestInfo} from "@khan/shared-core";
import {randomUUID} from "@khan/util-random-uuid";

import {
    mapContentKindToServerFormat,
    mapExplicitClassroomContextToServerFormat,
    type LearningTimeContent,
    type LearningTimeContent_ServerFormat,
    type LearningTimeExplicitClassroomContext,
    // eslint-disable-next-line camelcase -- _ServerFormat is the convention for server types
    type LearningTimeExplicitClassroomContext_ServerFormat,
    type LearningTimeInfo,
    type LearningTimeInfo_ServerFormat,
} from "./types.ts";
import {getSmokeTestUrlEncodedLearningTimeString} from "./util.ts";

// Format expected by /mark_conversions and /publish_event.
export type ConversionServerFormat = Readonly<{
    conversion: string;
    untrustedClientTimestamp: ISODateString;
    // Undefined if there are no extras.
    extraJson?: string;
    // Undefined if we are e.g. sending a continuation event as the first event.
    learningTimeInfo?: LearningTimeInfo_ServerFormat;
}>;

// Format expected by /publish_event. The rest of the properties come from the
// specific event schema but they're not important here.
export type AnalyticsEventServerFormat = Readonly<{
    _eventType: string;
    _schemaVersion: number;
    _eventTime: DateField;
    _origin: string;
    _uniqueId: string;
    _appVersion: string;
}>;

// General shape of a client-side conversion.
// This should match bingo_conversion_event.proto, except for `timestamp` which
// we store as milliseconds since the epoch, and which the protobuf stores as
// an ISO datestring under `untrustedClientTimestamp`. `event` is also passed
// as a separate parameter to the API.
type ClientConversion = Readonly<{
    conversion: string;
    timestamp: number;
    extraJson?: string;
    event?: AnalyticsEvent;
    // Undefined if we are e.g. sending a continuation event as the first event.
    learningTimeInfo?: LearningTimeInfo;
}>;

// e.g. "2011-11-11T11:11:00.011Z"
type ISODateString = string;

// /_bb/ sends this to a separate kotlin service that handles conversions.
export const markConversionsURL = "/api/internal/_bb/bigbingo/mark_conversions";
export const publishAnalyticsEventURL =
    "/api/internal/_analytics/publish_event";

/** Error messages the browser sends when it cancels a fetch request due to the
 * user navigating away from the page before it finishes.
 * Could also be caused by a CORS issue.
 * Safari localizes the error and some translations were common enough in
 * sentry that I included them here.
 */
const canceledMessages = [
    "Failed to fetch", // Chrome
    "NetworkError when attempting to fetch resource.", //Firefox
    "cancelled", // Safari
    "cancelado", // Safari Spanish/Porteguese
    "annulé", // Safari French
];

export const getURLWithEncodedLearningTime = (
    conversion: ConversionServerFormat,
): string => {
    try {
        const learningTimeSmokeTestEncodedString =
            getSmokeTestUrlEncodedLearningTimeString(
                conversion.learningTimeInfo,
            );
        return learningTimeSmokeTestEncodedString &&
            learningTimeSmokeTestEncodedString.length > 0
            ? `${markConversionsURL}?${learningTimeSmokeTestEncodedString}`
            : markConversionsURL;
    } catch (e: any) {
        // Since we only do this to support LTv2 smoke testing, we wouldn't
        // want an error to cause a conversion to not fire or have any
        // user-facing impact. Therefore we swallow any errors and move on.
        return markConversionsURL;
    }
};

export const getAnalyticsEventUrl = (
    event: AnalyticsEventServerFormat,
): string => {
    return `${publishAnalyticsEventURL}?type=${event._eventType}&v=${event._schemaVersion}`;
};

export function markConversionsWithExtras(
    conversions: ReadonlyArray<ClientConversion>,
): Promise<Response | null | undefined> {
    const promises = conversions
        // NOTE(slim): The following type annotation ensures that we are
        // sending data in the format expected by the server.
        .map((c: ClientConversion) => {
            const conversion = formatConversion(c);

            if (c.event) {
                // If we have analytics event data, send both conversion & event
                // data to the new /publish_event API.
                const event = formatEvent(c.timestamp, c.event);
                const url = getAnalyticsEventUrl(event);

                return khanFetch(url, {
                    method: "POST",
                    ...formUrlEncode({
                        conversion: JSON.stringify(conversion),
                        event: JSON.stringify(event),
                    }),
                });
            }

            // NOTE(jvoll): To support LTv2 smoke testing, we are making a
            // request for each conversion (instead of grouping multiple
            // conversions into a single `mark_conversions` request) and adding
            // a subset of learning time info as URL params. This allows our
            // smoke tests to assert that certain conversions are being fired
            // with the correct learning time info.
            // See ADR #208: https://docs.google.com/document/d/14QQ3x3uaJGf_cIValtiGcVv428L61lprjUqw245tu68/edit#heading=h.19s1eaqlee4q
            const url = getURLWithEncodedLearningTime(conversion);

            return khanFetch(url, {
                method: "POST",
                ...formUrlEncode({
                    conversion: JSON.stringify(conversion),
                }),
            });
        });

    // TODO(jvoll): There are very few callers that pass in multiple conversions
    // in a single call to this function. We should remove the capability to
    // mark multiple conversions at once and then just return a single promise.
    // This change is unfortuantely not well contained and would reach across
    // the entire code base. I could not find any callers that are currently
    // making use of the response object that this promise resolves to, they are
    // just waiting on it to resolve.
    return Promise.all(promises)
        .then((responses) => {
            return responses && responses.length > 0 ? responses[0] : null;
        })
        .catch((e) => {
            if (browserCanceledRequest(e)) {
                return;
            }
            const sentryData = getSentryDataForError(e);
            // TODO(somewhatabstract, WEB-2221): Code used to wait on sentry
            // promise here. May need to expand Log.error to return a promise
            // to recreate the same behavior.
            Log.error(sentryData.message, Errors.Internal, {
                cause: e,
                sentryData: {
                    contexts: {extras: sentryData.extras},
                    tags: {
                        LTv2: "true",
                    },
                    // We don't usually use custom fingerprinting however Sentry
                    // was having trouble grouping these errors and was merging
                    // many of the different errors triggered from this location.
                    // This should force Sentry to group these errors by their
                    // message instead of using the default grouping.
                    fingerprint: [sentryData.message],
                },
            });
            return null;
        });
}

export const getSentryDataForError = (
    e: ErrorWithResponse,
): {
    extras: SentryContext;
    message: string;
} => {
    // If the request failed with a non-200 status, e.response
    // should be set (see khan-fetch.checkStatus). Include response
    // information in Sentry reporting if we have it.
    const extras = {
        errorText: e.message,
        ...(e.response
            ? {statusCode: e.response.status, statusText: e.response.statusText}
            : null),
    } as const;

    const message = e.response
        ? `Got non-200 HTTP status from mark_conversions: ${e.response.status}`
        : getFailedToSendErrorString(e);

    return {
        extras,
        message,
    };
};

export const getFailedToSendErrorString = (e: Error): string => {
    const reason = e.message || "unknown";

    return `Failed to send mark_conversions request due to: ${reason}`;
};

/**
 * Transform input from a client-side representation containing both conversion
 * and event data into just the conversion data expected by the
 * /mark_conversions or /publish_event APIs.
 */
const formatConversion = (c: ClientConversion): ConversionServerFormat => {
    const {conversion, timestamp, extraJson, learningTimeInfo} = c;

    // Format conversion data as the server expects it
    return {
        conversion,
        untrustedClientTimestamp: new Date(timestamp).toISOString(),
        ...((extraJson ? {extraJson} : null) as any),
        ...(learningTimeInfo
            ? {
                  learningTimeInfo:
                      learningTimeInfoToServerFormat(learningTimeInfo),
              }
            : null),
    };
};

/**
 * Transform input event data into just the event data expected by the
 * /publish_event API.
 */
const formatEvent = (
    timestamp: number,
    event: AnalyticsEvent,
): AnalyticsEventServerFormat => {
    const {eventType, eventSchemaVersion, ...rest} = event;

    // If we're on a dev server, prefix the app version with "dev:" so we can
    // easily tell which events are coming from dev servers.
    const devPrefix = RequestInfo.IS_DEV_SERVER ? "dev:" : "";

    return {
        _eventType: eventType,
        _schemaVersion: eventSchemaVersion,
        _eventTime: new DateField(new Date(timestamp)),
        _origin: "client",
        _uniqueId: randomUUID(),
        _appVersion: `${devPrefix}${DeployInfo.staticVersion}`,
        ...rest,
    };
};

/**
 * Detect if the failure was due to the use navigating away from the page and
 * the browser canceling the request as a result. By detecting and not logging
 * this type of error we stabalize our e2e tests and keep our sentry logs cleaner
 * One such failing test was:
 * assignments.end_to_end.class_assignments_smoketest.ClassAssignmentsTest.test_empty_state
 */
export const browserCanceledRequest = (error: Error): boolean => {
    return canceledMessages.includes(error.message);
};

const learningTimeInfoToServerFormat = (
    learningTimeInfo: LearningTimeInfo,
): LearningTimeInfo_ServerFormat => {
    const {content, explicitClassroomContext, ...rest} = learningTimeInfo;

    return {
        ...rest,
        ...learningTimeContentToServerFormat(content),
        ...learningTimeExplicitClassroomContextToServerFormat(
            explicitClassroomContext,
        ),
    };
};

const learningTimeContentToServerFormat = (
    content?: LearningTimeContent,
): {
    content: LearningTimeContent_ServerFormat;
} | null => {
    if (!content) {
        return null;
    }
    const {kind, ...rest} = content;
    return {
        content: {
            ...rest,
            kind: mapContentKindToServerFormat(kind),
        },
    };
};

const learningTimeExplicitClassroomContextToServerFormat = (
    explicitClassroomContext?: LearningTimeExplicitClassroomContext,
): {
    // eslint-disable-next-line camelcase -- _ServerFormat is the convention for server types
    explicitClassroomContext: LearningTimeExplicitClassroomContext_ServerFormat;
} | null => {
    if (!explicitClassroomContext) {
        return null;
    }
    return {
        explicitClassroomContext: mapExplicitClassroomContextToServerFormat(
            explicitClassroomContext,
        ),
    };
};
